import {
    Heart, Search, Bell, MessageSquare, ChevronRight,
    Plus, MessageCircle, Share2, Filter, TrendingUp,
    Users, Calendar, Pin, Lock, Send, X, Edit, Trash2,
    MoreVertical, Flag, Bookmark, ArrowLeft, Clock,
    Wind, Activity, UtensilsCrossed, Home, Pill
} from 'lucide-react';
import { useState, useEffect } from 'react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { communityService } from '../services/community';
import styles from './Community.module.css';


const defaultCategories = [
    { id: 1, name: 'Astma', description: 'Del erfaringer om astma og behandling', icon: 'Wind', color: '#7c898b', postCount: 0 },
    { id: 2, name: 'Allergi', description: 'Spørgsmål og støtte om allergi', icon: 'Activity', color: '#d4183d', postCount: 0 },
    { id: 3, name: 'Kost', description: 'Mad, intolerancer og kostråd', icon: 'UtensilsCrossed', color: '#b68b5e', postCount: 0 },
    { id: 4, name: 'Hjem og miljø', description: 'Indeklima, støv, pollen og rengøring', icon: 'Home', color: '#5f7f8b', postCount: 0 },
    { id: 5, name: 'Medicin', description: 'Inhalatorer, medicin og bivirkninger', icon: 'Pill', color: '#8a6f9b', postCount: 0 },
    { id: 6, name: 'Generelt', description: 'Andre spørgsmål og gode råd', icon: 'Users', color: '#7c898b', postCount: 0 },
];

export default function Community() {
    const [categories, setCategories] = useState([]);
    const [posts, setPosts] = useState([]);
    const [allPosts, setAllPosts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreatePost, setShowCreatePost] = useState(false);
    const [showReplyBox, setShowReplyBox] = useState(false);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('categories'); // 'categories', 'posts', 'post-detail'
    const [sortBy, setSortBy] = useState('popular'); // 'popular', 'newest', 'mostCommented'
    const [lastSearchResults, setLastSearchResults] = useState([]);

    const { slug } = useParams();

    const slugify = (s = '') => s.toString().toLowerCase().trim().replace(/[^a-z0-9æøå\s-]/g, '').replace(/\s+/g, '-');

    const visibleCategories = categories.length > 0 ? categories : defaultCategories;


    // Get user role for admin functionality
    const userRole = localStorage.getItem('role');
    const isAdmin = userRole === 'admin' || userRole === 'superadmin';

    // Icon mapping for categories
    const getCategoryIcon = (iconName) => {
        const icons = {
            Wind, Activity, UtensilsCrossed, Home, Pill, Users
        };
        return icons[iconName] || Users;
    };

    // Form states
    const [newPost, setNewPost] = useState({
        title: '',
        content: '',
        categoryId: null
    });
    const [newComment, setNewComment] = useState('');

    useEffect(() => {
        loadCategories();
        loadAllPosts();
    }, []);

    // when categories load or url slug changes, select matching category
    useEffect(() => {
        if (!slug || categories.length === 0) return;
        const match = categories.find(c => slugify(c.name) === slug);
        if (match) {
            setSelectedCategory(match);
            setSelectedPost(null);
            setView('posts');
        }
    }, [slug, categories]);

    // Handle going back to main community page (when slug is undefined)
    useEffect(() => {
        if (!slug && view !== 'categories') {
            setView('categories');
            setSelectedCategory(null);
            setSelectedPost(null);
        }
    }, [slug]);

    useEffect(() => {
        if (selectedCategory) {
            loadPosts(selectedCategory.id);
        }
    }, [selectedCategory]);

    useEffect(() => {
        if (selectedPost) {
            loadComments(selectedPost.id);
        }
    }, [selectedPost]);

    // Handle search results tracking
    useEffect(() => {
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            const filtered = posts.filter(post =>
                post.title.toLowerCase().includes(query) ||
                post.content.toLowerCase().includes(query)
            );

            if (filtered.length > 0) {
                setLastSearchResults([...filtered]);
            }
        } else {
            setLastSearchResults([]);
        }
    }, [searchQuery, posts]);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const data = await communityService.getCategories();
            setCategories(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading categories:', error);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const loadAllPosts = async () => {
        try {
            const data = await communityService.getPosts();
            setAllPosts(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading all posts:', error);
            setAllPosts([]);
        }
    };

    const loadPosts = async (categoryId) => {
        try {
            setLoading(true);
            const data = await communityService.getPosts(categoryId);
            setPosts(Array.isArray(data) ? data : []);
            setView('posts');
        } catch (error) {
            console.error('Error loading posts:', error);
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };

    const loadComments = async (postId) => {
        try {
            const data = await communityService.getComments(postId);
            setComments(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading comments:', error);
            setComments([]);
        }
    };

    const handleCategoryClick = (category) => {
        // kept for backward compatibility; prefer Link navigation
        setSelectedCategory(category);
        setSelectedPost(null);
    };

    const handlePostClick = async (post) => {
        setSelectedPost(post);
        setView('post-detail');
    };

    const handleCreatePost = async (e) => {
        e.preventDefault();
        try {
            const postData = {
                ...newPost,
                categoryId: selectedCategory.id
            };
            await communityService.createPost(postData);
            setShowCreatePost(false);
            setNewPost({ title: '', content: '', categoryId: null });
            loadPosts(selectedCategory.id);
        } catch (error) {
            console.error('Error creating post:', error);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        try {
            await communityService.addComment(selectedPost.id, { content: newComment });
            setNewComment('');
            setShowReplyBox(false);
            loadComments(selectedPost.id);
            // Update comment count
            setSelectedPost(prev => ({
                ...prev,
                commentCount: prev.commentCount + 1
            }));
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    const handlePinPost = async (postId, isCurrentlyPinned) => {
        try {
            if (isCurrentlyPinned) {
                await communityService.unpinPost(postId);
            } else {
                await communityService.pinPost(postId);
            }
            // Reload posts to reflect the change
            if (selectedCategory) {
                loadPosts(selectedCategory.id);
            }
            // Update selected post if it's the one being pinned/unpinned
            if (selectedPost && selectedPost.id === postId) {
                setSelectedPost(prev => ({ ...prev, isPinned: !isCurrentlyPinned }));
            }
        } catch (error) {
            console.error('Error pinning/unpinning post:', error);
        }
    };

    // Likes are removed from the community UI by design.

    const handleBackToCategories = () => {
        setView('categories');
        setSelectedCategory(null);
        setSelectedPost(null);
    };

    const handleBackToPosts = () => {
        setView('posts');
        setSelectedPost(null);
    };

    const filteredPosts = posts.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Enhanced search and sorting logic
    const getProcessedPosts = () => {
        let processedPosts = [...posts];

        // Apply search filter if there's a search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            processedPosts = processedPosts.filter(post =>
                post.title.toLowerCase().includes(query) ||
                post.content.toLowerCase().includes(query)
            );

            // If no results and we have a previous search result, show the last valid results
            if (processedPosts.length === 0 && lastSearchResults.length > 0) {
                processedPosts = [...lastSearchResults];
            }
        }

        // Sort posts: pinned posts first, then by selected criteria
        processedPosts.sort((a, b) => {
            // Pinned posts always come first
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;

            // Then sort by the selected criteria
            switch (sortBy) {
                case 'newest':
                    return new Date(b.createdAt || b.time) - new Date(a.createdAt || a.time);
                case 'mostCommented':
                    return (b.commentCount || 0) - (a.commentCount || 0);
                case 'popular':
                default:
                    // For popular, prioritize by comment count, then by recency
                    const commentDiff = (b.commentCount || 0) - (a.commentCount || 0);
                    if (commentDiff !== 0) return commentDiff;
                    return new Date(b.createdAt || b.time) - new Date(a.createdAt || a.time);
            }
        });

        return processedPosts;
    };

    const processedPosts = getProcessedPosts();

    if (loading && categories.length === 0) {
        return <div className={styles.communityLoading}>Indlæser community...</div>;
    }

    return (
        <div className={styles.community}>

            {/* Main Content */}
            <main className={styles.communityMain}>
                {/* Breadcrumb Navigation */}
                <div className={styles.breadcrumb}>
                    <button
                        className={styles.breadcrumbLink}
                        onClick={handleBackToCategories}
                    >
                        Community
                    </button>
                    {selectedCategory && (
                        <>
                            <ChevronRight size={16} />
                            <button
                                className={styles.breadcrumbLink}
                                onClick={handleBackToPosts}
                            >
                                {selectedCategory.name}
                            </button>
                        </>
                    )}
                    {selectedPost && (
                        <>
                            <ChevronRight size={16} />
                            <span className={styles.breadcrumbCurrent}>{selectedPost.title}</span>
                        </>
                    )}
                </div>

                {/* Categories View */}
                {view === 'categories' && (
                    <div className={styles.categoriesView}>
                        <div className={styles.viewHeader}>
                            <div>
                                <h1 className={styles.viewTitle}>Velkommen til Community</h1>
                                <p className={styles.viewSubtitle}>
                                    Del dine erfaringer, stil spørgsmål og find støtte fra andre med astma og allergi
                                </p>
                            </div>
                        </div>

                        <div className={styles.categoriesGrid}>
                            {visibleCategories.length > 0 ? (
                                visibleCategories.map(category => {
                                    const cslug = slugify(category.name);
                                    return (
                                        <Link
                                            key={category.id}
                                            to={`/community/${cslug}`}
                                            className={styles.categoryCard}
                                        >
                                            <div className={styles.categoryIcon} style={{ backgroundColor: (category.color || '#7c898b') + '20' }}>
                                                {React.createElement(getCategoryIcon(category.icon), {
                                                    size: 28,
                                                    color: category.color || '#7c898b'
                                                })}
                                            </div>
                                            <div className={styles.categoryInfo}>
                                                <h3 className={styles.categoryName}>{category.name}</h3>
                                                <p className={styles.categoryDescription}>{category.description}</p>
                                                <div className={styles.categoryStats}>
                                                    <div className={styles.stat}>
                                                        <MessageSquare size={14} />
                                                        <span>{category.postCount} indlæg</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <ChevronRight className={styles.categoryArrow} size={20} />
                                        </Link>
                                    );
                                })
                            ) : (
                                <div className={styles.emptyState}>
                                    <p>Ingen kategorier tilgængelige.</p>
                                </div>
                            )}
                        </div>

                        {/* Popular vs New Posts Section */}
                        <div className={styles.featuredPostsSection}>
                            <div className={styles.featuredPostsGrid}>
                                {/* Most Popular Post */}
                                <div className={styles.featuredColumn}>
                                    <h2 className={styles.sectionTitle}>
                                        <TrendingUp size={20} />
                                        Mest populær
                                    </h2>
                                    {allPosts.length > 0 ? (
                                        <div
                                            className={`${styles.featuredPostCard} ${styles.popularPost}`}
                                            onClick={() => {
                                                const mostPopular = [...allPosts].sort((a, b) => (b.commentCount || 0) - (a.commentCount || 0))[0];
                                                handlePostClick(mostPopular);
                                            }}
                                        >
                                            {(() => {
                                                const mostPopular = [...allPosts].sort((a, b) => (b.commentCount || 0) - (a.commentCount || 0))[0];
                                                return (
                                                    <>
                                                        <div className={styles.featuredPostHeader}>
                                                            <div className={styles.postAuthor}>{mostPopular.author}</div>
                                                            <div className={styles.postTime}>
                                                                <Calendar size={12} />
                                                                {mostPopular.time}
                                                            </div>
                                                        </div>
                                                        <h3 className={styles.featuredPostTitle}>{mostPopular.title}</h3>
                                                        <p className={styles.featuredPostExcerpt}>{mostPopular.content}</p>
                                                        <div className={styles.featuredPostStats}>
                                                            <div className={styles.stat}>
                                                                <MessageCircle size={16} />
                                                                <span>{mostPopular.commentCount || 0} svar</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <div className={styles.emptyFeatured}>Ingen indlæg endnu</div>
                                    )}
                                </div>

                                {/* Newest Posts */}
                                <div className={styles.featuredColumn}>
                                    <h2 className={styles.sectionTitle}>
                                        <Clock size={20} />
                                        Seneste indlæg
                                    </h2>
                                    <div className={styles.newPostsList}>
                                        {allPosts.length > 0 ? (
                                            allPosts.sort((a, b) => new Date(b.createdAt || b.time) - new Date(a.createdAt || a.time)).slice(0, 4).map(post => (
                                                <div
                                                    key={post.id}
                                                    className={styles.newPostItem}
                                                    onClick={() => handlePostClick(post)}
                                                >
                                                    <div className={styles.newPostMeta}>
                                                        <div className={styles.newPostAuthor}>{post.author}</div>
                                                        <div className={styles.newPostTime}>{post.time}</div>
                                                    </div>
                                                    <div className={styles.newPostTitle}>{post.title}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className={styles.emptyFeatured}>Ingen indlæg endnu</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Posts List View */}
                {view === 'posts' && selectedCategory && (
                    <div className={styles.postsView}>
                        <div className={styles.viewHeader}>
                            <div>
                                <button className={styles.backButton} onClick={handleBackToCategories}>
                                    <ArrowLeft size={20} />
                                    Tilbage til kategorier
                                </button>
                                <h1 className={styles.viewTitle}>{selectedCategory.name}</h1>
                                <p className={styles.viewSubtitle}>{selectedCategory.description}</p>
                            </div>
                            <button
                                className={styles.createPostButton}
                                onClick={() => setShowCreatePost(true)}
                            >
                                <Plus size={20} />
                                Nyt indlæg
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className={styles.searchSection}>
                            <div className={styles.searchInputWrapper}>
                                <Search size={18} />
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Søg i indlæg..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                {searchQuery && (
                                    <button
                                        className={styles.clearSearchBtn}
                                        onClick={() => setSearchQuery('')}
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Posts List */}
                        <div className={styles.postsList}>
                            {processedPosts.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <MessageSquare size={48} />
                                    <p>{searchQuery ? 'Ingen resultater fundet' : 'Ingen indlæg endnu'}</p>
                                    <button
                                        className={styles.createPostButton}
                                        onClick={() => setShowCreatePost(true)}
                                    >
                                        Opret det første indlæg
                                    </button>
                                </div>
                            ) : (
                                processedPosts.map(post => (
                                    <div
                                        key={post.id}
                                        className={styles.postCard}
                                        onClick={() => handlePostClick(post)}
                                    >
                                        <div className={styles.postHeader}>
                                            <div className={styles.postMetaCompact}>
                                                <div className={styles.postAuthor}>{post.author}</div>
                                                <div className={styles.postTime}>
                                                    <Calendar size={12} />
                                                    {post.time}
                                                </div>
                                            </div>
                                            <div className={styles.postHeaderActions}>
                                                {post.isPinned && (
                                                    <div className={`${styles.postBadge} pinned`}>
                                                        <Pin size={14} />
                                                        Fastgjort
                                                    </div>
                                                )}
                                                {isAdmin && (
                                                    <button
                                                        className={styles.iconButton}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePinPost(post.id, post.isPinned);
                                                        }}
                                                        title={post.isPinned ? 'Fjern fastgøring' : 'Fastgør indlæg'}
                                                    >
                                                        <Pin size={16} color={post.isPinned ? '#d4183d' : '#7c898b'} fill={post.isPinned ? '#d4183d' : 'none'} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <h3 className={styles.postTitle}>{post.title}</h3>
                                        <p className={styles.postExcerpt}>{post.content}</p>

                                        <div className={styles.postFooter}>
                                            <div className={styles.postStats}>
                                                <div className={styles.stat}>
                                                    <MessageCircle size={14} />
                                                    <span>{post.commentCount} svar</span>
                                                </div>
                                            </div>
                                            <button className={styles.shareButton}>
                                                <Share2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Post Detail View */}
                {view === 'post-detail' && selectedPost && (
                    <div className={styles.postDetailView}>
                        <button className={styles.backButton} onClick={handleBackToPosts}>
                            <ArrowLeft size={20} />
                            Tilbage til indlæg
                        </button>

                        {/* Post Content */}
                        <div className={styles.postDetailCard}>
                            <div className={styles.postDetailHeader}>
                                <div>
                                    <div className={styles.postMetaLarge}>
                                        <div className={styles.postAuthor}>{selectedPost.author}</div>
                                        <div className={styles.postTime}>
                                            <Calendar size={14} />
                                            {selectedPost.time}
                                        </div>
                                    </div>
                                    {selectedPost.isPinned && (
                                        <div className={styles.pinnedBadge}>
                                            <Pin size={14} />
                                            Fastgjort indlæg
                                        </div>
                                    )}
                                </div>
                                {isAdmin && (
                                    <button
                                        className={styles.pinButton}
                                        onClick={() => handlePinPost(selectedPost.id, selectedPost.isPinned)}
                                        title={selectedPost.isPinned ? 'Fjern fastgøring' : 'Fastgør indlæg'}
                                    >
                                        <Pin size={20} fill={selectedPost.isPinned ? 'currentColor' : 'none'} />
                                        {selectedPost.isPinned ? 'Fjern fastgøring' : 'Fastgør indlæg'}
                                    </button>
                                )}
                            </div>

                            <h1 className={styles.postDetailTitle}>{selectedPost.title}</h1>
                            <div className={styles.postDetailContent}>{selectedPost.content}</div>

                            {selectedPost.tags && selectedPost.tags.length > 0 && (
                                <div className={styles.postTags}>
                                    {selectedPost.tags.map(tag => (
                                        <span key={tag} className={styles.tag}>#{tag}</span>
                                    ))}
                                </div>
                            )}

                            <div className={styles.postActions}>
                                <button
                                    className={styles.actionButton}
                                    onClick={() => setShowReplyBox(!showReplyBox)}
                                >
                                    <MessageCircle size={18} />
                                    <span>Svar ({selectedPost.commentCount})</span>
                                </button>
                                <button className={styles.actionButton}>
                                    <Share2 size={18} />
                                    <span>Del</span>
                                </button>
                                <button className={styles.actionButton}>
                                    <Bookmark size={18} />
                                    <span>Gem</span>
                                </button>
                            </div>
                        </div>

                        {/* Reply Box */}
                        {showReplyBox && (
                            <div className={styles.replyBox}>
                                <div className={styles.replyBoxHeader}>
                                    <h3>Skriv et svar</h3>
                                    <button
                                        className={styles.closeButton}
                                        onClick={() => setShowReplyBox(false)}
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                                <form onSubmit={handleAddComment}>
                                    <textarea
                                        className={styles.replyTextarea}
                                        placeholder="Del dine tanker..."
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        rows="4"
                                    />
                                    <div className={styles.replyActions}>
                                        <button
                                            type="button"
                                            className={styles.cancelButton}
                                            onClick={() => {
                                                setShowReplyBox(false);
                                                setNewComment('');
                                            }}
                                        >
                                            Annuller
                                        </button>
                                        <button type="submit" className={styles.submitButton}>
                                            <Send size={16} />
                                            Send svar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Comments Section */}
                        <div className={styles.commentsSection}>
                            <h2 className={styles.commentsTitle}>
                                Svar ({comments.length})
                            </h2>

                            {comments.length === 0 ? (
                                <div className={styles.emptyComments}>
                                    <MessageCircle size={48} />
                                    <p>Ingen svar endnu</p>
                                    <p className={styles.emptySubtitle}>Vær den første til at svare!</p>
                                    <button
                                        className={styles.createPostButton}
                                        onClick={() => setShowReplyBox(true)}
                                    >
                                        Skriv et svar
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.commentsList}>
                                    {comments.map(comment => (
                                        <div key={comment.id} className={styles.commentCard}>
                                            <div className={styles.commentHeader}>
                                                <div className={styles.commentMeta}>
                                                    <div className={styles.commentAuthor}>{comment.author}</div>
                                                    <div className={styles.commentTime}>{comment.time}</div>
                                                </div>
                                                <button className={styles.iconButton}>
                                                    <MoreVertical size={16} />
                                                </button>
                                            </div>
                                            <div className={styles.commentContent}>{comment.content}</div>
                                            <div className={styles.commentActions}>
                                                <button className={styles.commentAction}>
                                                    <MessageCircle size={14} />
                                                    Svar
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>

            {/* Create Post Modal */}
            {showCreatePost && (
                <div className={styles.modalOverlay} onClick={() => setShowCreatePost(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Opret nyt indlæg</h2>
                            <button
                                className={styles.closeButton}
                                onClick={() => setShowCreatePost(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleCreatePost}>
                            <div className={styles.formGroup}>
                                <label htmlFor="post-title">Titel</label>
                                <input
                                    id="post-title"
                                    type="text"
                                    className={styles.formInput}
                                    placeholder="Skriv en beskrivende titel..."
                                    value={newPost.title}
                                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="post-content">Indhold</label>
                                <textarea
                                    id="post-content"
                                    className={styles.formTextarea}
                                    placeholder="Del dine tanker, erfaringer eller stil et spørgsmål..."
                                    value={newPost.content}
                                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                                    rows="8"
                                    required
                                />
                            </div>
                            <div className={styles.modalActions}>
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={() => setShowCreatePost(false)}
                                >
                                    Annuller
                                </button>
                                <button type="submit" className={styles.submitButton}>
                                    <Send size={16} />
                                    Opret indlæg
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
